import loginView from './loginView.html';
import spinner from 'components/sharpr/spinner/SpinnerSingleton';
import './loginComponentStyles.css';
import _ from 'lodash';
import { markAsLoggingIn } from '../auth/AuthInterceptor';

export function loginComponent() {
	return {
		bindings: {
			prompt: '<',
			forwardTo: '@?',
			onAfterLogin: '&?',
			themeData: '<?',
		},
		controllerAs: 'vm',
		controller: loginComponentCtrl,
		template: loginView,
	};
}

export class loginComponentCtrl {
	constructor($scope, Api, Logger, $location, $state, $timeout, Auth, EventTracker, SecureStorage, modal, Cookies) {
		'ngInject';
		this.Api = Api;
		this.Logger = Logger;
		this.Auth = Auth;
		this.$location = $location;
		this.$timeout = $timeout;
		this.$state = $state;
		this.EventTracker = EventTracker;
		this.SecureStorage = SecureStorage;
		this.modal = modal;
		this.Cookies = Cookies;
		this.$scope = $scope;
	}

	$onInit() {
		this.email = '';
		this.password = '';
		this.rememberMe = true;
		this.panelIdx = 0;
		this.onPasswordPanel = false;
		this.PASSWORD_PANEL_TYPE_DEFAULT = 0;
		this.PASSWORD_PANEL_TYPE_MSTEAMS = 1;
		this.OAUTH_AUTHORIZE_PATH = '/oauth2/authorize';
		this.passwordPanelType = this.getPasswordPanelType();
		this.maybePrepopulateEmail();
		this.headerStyle = this.getHeaderStyle();
		this.buttonStyle = this.getButtonStyle();
		this.logoUrl = this.getLogoUrl();
		this.$timeout(() => {
			this.appVersion = this.Logger.getAppVersion();
		}, 100);
		this.isMobile = window.isMobile;

		this.$scope.$watch('vm.panelIdx', newValue => {
			this.Logger.write('APP_LOGIN_DEBUG', 'Updating password panel to idx: ' + newValue, {
				btFile: 'loginComponent.js',
				btLine: 40,
			});
			if (newValue == 1) {
				setTimeout(() => {
					this.onPasswordPanel = true;
				}, 250);
			} else {
				this.onPasswordPanel = false;
				spinner.stopAll();
			}
		});
	}

	/*
	 * Handler for password-panel-display determination
	 * @returns Integer
	 */
	getPasswordPanelType() {
		let ret = this.PASSWORD_PANEL_TYPE_DEFAULT;
		if (this.isPasswordPanelMsTeams()) {
			ret = this.PASSWORD_PANEL_TYPE_MSTEAMS;
		}
		return ret;
	}

	/*
	 * Determines whether or not the password panel is for MsTeams
	 * @returns Boolean
	 */
	isPasswordPanelMsTeams() {
		if (!this.forwardTo) {
			return false;
		}

		const decodedForward = decodeURIComponent(this.forwardTo);
		if (!decodedForward.includes('?') || !decodedForward.includes('&')) {
			return false;
		}

		const params = new URLSearchParams(decodedForward.split('?')[1]);
		const redirectUri = params.get('redirect_uri');
		if (!redirectUri || !redirectUri.includes('token.botframework.com')) {
			return false;
		}

		return true;
	}

	maybePrepopulateEmail() {
		const email = this.$location.search().email;
		if (email) {
			// we came from another stack so we can populate email
			this.email = email;
			// remove email from the URL
			this.$location.search('email', undefined);
			// move login panel to the second pane
			this.submitEmail();
		}
	}
	conditionsOfUse() {
		this.Api.get('/legal', {}, { Authorization: 'Public' })
			.then(response => {
				this.modal.html({
					text: response,
					heading: 'Conditions of Use',
					width: 'full',
					height: 'full',
				});
			})
			.catch(() => {
				console.error('Unable to get legal content');
			});
	}

	submitEmail() {
		markAsLoggingIn();
		this.Logger.write('APP_LOGIN_DEBUG', 'Submitted email address: ' + this.email, {
			btFile: 'loginComponent.js',
			btLine: 57,
		});
		this.passwordError = '';
		if (!this.email || this.email.length === 0) {
			this.emailError = 'Please enter a valid email address and try again.';
			spinner.stopAll();
			return;
		}
		// Ask API what type of auth we need to do
		this.Auth.getTypeByEmail(this.email).then(this._gotAuthType.bind(this), this._failedAuthType.bind(this));
	}

	getHeaderStyle() {
		if (!this.themeData) {
			return {};
		}
		const onLightOrDark = this.themeData.header === 'white' ? 'on_light' : 'on_dark';
		return {
			'background-color': onLightOrDark === 'on_light' ? '#ffffff' : '#000000',
			'border-bottom': onLightOrDark === 'on_light' ? '1px solid #E1E1E1' : 'none',
		};
	}

	getLogoUrl() {
		if (!this.themeData) {
			return '/assets/theme/sharpr/on-dark.png?v=ef4au';
		}
		const onLightOrDark = this.themeData.header === 'white' ? 'on_light' : 'on_dark';
		return this.themeData[onLightOrDark + '_url'];
	}

	getButtonStyle() {
		if (!this.themeData) {
			return {};
		}
		return { 'background-color': this.themeData.button1 };
	}

	// resolved with GET /auth/type
	_gotAuthType(authType) {
		const message = authType ? `Got /auth/type: ${authType.type}` : 'authType is falsy';
		this.Logger.write('APP_LOGIN_DEBUG', message, {
			authType,
			btFile: 'loginComponent.js',
			btLine: 78,
		});
		if (!authType) {
			// _failedAuthType should run automatically on a failed api call,
			/// but sometimes it doesn't for reasons unknown.
			this._failedAuthType();
		}
		if (authType.base_url !== window.BASE_URL && authType.url) {
			// vanity url
			let redirectUrl = authType.url;
			if (this._isOAuthAuthorize()) {
				// used to set the post-auth destination back to the oauth-authorize page
				const forwardTo = this.forwardTo.replace(window.BASE_URL, '');
				redirectUrl += `${redirectUrl.includes('?') ? '&' : '?'}forwardTo=${encodeURIComponent(forwardTo)}`;
			}
			window.location.href = redirectUrl;
		} else if (authType.type === 'saml' || authType.type === 'openid') {
			// ADFS SAML or Google OAuth
			this._startSsoProcess(authType);
		} else {
			// Normal password login
			this._askForPassword(authType);
		}
	}

	_isOAuthAuthorize() {
		if (!this.forwardTo) {
			return false;
		}

		const path = this.forwardTo.split('?')[0].replace(window.BASE_URL, '');
		return path === this.OAUTH_AUTHORIZE_PATH;
	}

	// rejected from GET /auth/type
	_failedAuthType() {
		this.Logger.write('APP_LOGIN_DEBUG', `Error getting authtype for email ${this.email}`, {
			btFile: 'loginComponent.js',
			btLine: 99,
		});
		// Email not found or network error after calling /api/v2/auth/type
		this._track('login_fail', { authtype: '(System error)' });
		this.emailError = 'We were unable to find that email. Please try again.';
	}

	// called when auth type is "saml" or "openid"
	_startSsoProcess(authType) {
		this.Logger.write('APP_LOGIN_DEBUG', `Starting SSO Process for email ${this.email}`, {
			authType,
			btFile: 'loginComponent.js',
			btLine: 111,
		});
		this._track('sso_handoff', authType);
		if (authType.type === 'openid' && window.plugins && window.plugins.googleplus) {
			// mobile app oauth plugin
			this._startGoogleAuth(authType);
		} else if (authType.type === 'openid') {
			// bookmarklet or normal login
			window.location = authType.url + '?forwardTo=' + encodeURIComponent(window.location.href);
		} else if (window.cordova) {
			// SAML on mobile: open the InAppBrowser and point it to the URL specified by the API response
			const win = window.open(authType.url, '_blank', 'location=yes,closebuttoncaption=Close,enableViewportScale=yes');
			win.addEventListener('loaderror', this._getLoadErrorCallback(win));
			win.addEventListener('loadstop', this._getLoadStopCallback(win, authType));
		} else if (window === window.top) {
			// we are in our own window (e.g. main login page)
			// handle forwardTo, as in the case of /p/{postId} direct links
			const forwardTo = this.forwardTo.replace(window.BASE_URL, '');
			if (forwardTo != 'hub.loggedin.PostListing' && forwardTo != '/login') {
				const ngDest = JSON.stringify([escape(forwardTo)]);
				document.cookie = `ngDest=${ngDest}; max-age=86400; path=/; secure`;
			}
			// if we are using cordova, the url may be "/sso/saml" that handles JWT
			// But this if block is for non-cordova situations where we want to go through hub?sso=code
			// See models/user.php->getAuthType() where it says "/sso/saml" instead of hub?sso=code
			let url = String(authType.url).replace('/sso/saml/', '/hub');
			if (forwardTo) {
				url += (url.includes('?') ? '&' : '?') + `forwardTo=${encodeURIComponent(forwardTo)}`;
			}
			window.location = url;
		} else {
			// show button to pop open saml login
			this.authType = authType;
			this.$timeout(() => {
				this.ssoAuthAlert = authType.msg || 'Please log in with your intranet account.';
			});
		}
	}

	popupAuth() {
		// we are in bookmarklet or other iframe
		const url = '/posts/bookmarklet_sso?sso=' + this.authType.sp;
		const left = screen.width - 510;
		const top = 10;
		window.open(url, 'auth', 'menubar=no, status=no, toolbar=no, width=500, height=400, left=' + left + ', top=' + top);
		window.addEventListener(
			'message',
			evt => {
				if (evt.data == 'sso-success') {
					window.location.reload();
				}
			},
			false
		);
	}

	// send login-related events to the event tracker
	_track(event, authType) {
		this.EventTracker.save(event, {
			email: this.email,
			authtype: authType.type,
			url: authType.url || window.BASE_URL,
		});
	}

	// called by _startSsoProcess when url == 'https://sharpr.com/sso/openid/$spCode'
	_startGoogleAuth(authType) {
		this.Logger.write('APP_LOGIN_DEBUG', `Starting Google SSO Process for email ${this.email}`, {
			authType,
			btFile: 'loginComponent.js',
			btLine: 159,
		});
		window.plugins.googleplus.login(
			{
				webClientId: authType.google_app_client_id,
			},
			obj => {
				// /auth/google_verify (private endpoint)
				this.Auth.verifyGoogleIdToken(obj.idToken, this.email, obj.displayName).then(data => {
					if (!data) {
						this.emailError = 'Authentication failed. Please try again later.';
						this._track('login_failed', authType);
						return;
					}
					this._track('login_success', authType);
					window.location.href = this.$state.href('mobile.loggedin.PostListing');
				});
			},
			msg => {
				console.log('error:');
				console.log(msg);
				(navigator.notification || window).alert('Authentication failed. Please try again.');
			}
		);
	}

	// called when authType.type is "password"
	_askForPassword(authType) {
		this.Logger.write('APP_LOGIN_DEBUG', `Asking for password for email ${this.email}`, {
			authType,
			btFile: 'loginComponent.js',
			btLine: 221,
		});
		this._track('login_authtype', authType);
		// handle the case where we are phonegap and need to point to wm.sharpr.com or cibc.sharpr.com
		if (authType.base_url) {
			window.BASE_URL = authType.base_url;
		}
		this.SecureStorage.set('base_url', window.BASE_URL);
		try {
			localStorage.setItem('base_url', window.BASE_URL);
		} catch (e) {
			// nothing to to here
		}
		// clear error text
		this.emailError = '';
		// show password panel
		this.panelIdx = 1;
		setTimeout(() => {
			try {
				document.querySelector('input[type=password]').focus();
			} catch (e) {
				// nothing to to here
			}
		}, 350);
	}

	// callback for SSO InAppBrowser loaderror event
	_getLoadErrorCallback(win) {
		return evt => {
			// see https://cordova.apache.org/docs/en/latest/reference/cordova-plugin-inappbrowser/#inappbrowserevent-properties
			this.Logger.write(
				'APP_LOGIN_DEBUG',
				`Error in SSO Process for email ${this.email}: [${evt.code}] ${evt.message} (${evt.url})`,
				{
					btFile: 'loginComponent.js',
					btLine: 244,
				}
			);
			// Some network error caused a page load error during the SSO process
			// Note that if evt.code -999 (NSURLErrorCancelled) loading may have been interrupted by the success screen or login screen
			// https://developer.apple.com/documentation/foundation/1508628-url_loading_system_error_codes/nsurlerrorcancelled?language=objc
			if (evt.code == -999) {
				return;
			}
			// Below we use setTimeout() to make sure the error is fatal
			// (So note below that we do clearTimeout(this.errorTimeout) in the success callback)
			this.errorTimeout = setTimeout(() => {
				win.close();
				(navigator.notification || window).alert(
					'There was a network error connecting to your SSO login page. Please try again.'
				);
			}, 5000);
		};
	}

	// callback for SSO InAppBrowser loadstop event
	_getLoadStopCallback(win, authType) {
		return evt => {
			this.Logger.write('APP_LOGIN_DEBUG', `SSO Page loaded`, {
				authType,
				url: evt.url,
				btFile: 'loginComponent.js',
				btLine: 261,
			});
			if (authType.type == 'openid' && evt.url.match(/^https:\/\/accounts\.google\.com/)) {
				// We are on the google oauth success page and need to hack it.
				// By default, google oauth tries to invoke window.close() but that
				// will fail because the window was opened by Phonegaps InAppBrowser.
				// So we nuke the close function
				win.executeScript({ code: `window.close = function() {}` });
				// and then find the Google jwt inside document.body.innerHTML
				// and POST it to our success page
				win.executeScript({ code: this._getOAuthSelfSubmitHtml() });
			} else {
				// We are on some page in the SSO process; check if it is our own success page
				win.executeScript({ code: "sessionStorage.getItem('jwt')" }, this._getSsoCallback(win));
			}
		};
	}

	// build the code to inject into the InAppBrowser window for Google Oauth
	_getOAuthSelfSubmitHtml() {
		this.Logger.write('APP_LOGIN_DEBUG', `Attempting to read JWT from body.innerHTML for email ${this.email}`, {
			btFile: 'loginComponent.js',
			btLine: 288,
		});
		return `
			var jwt = document.body.innerHTML.match(/"(ey.+?)"/)[1];
			document.body.innerHTML +=
				'<form action="${window.BASE_URL}/sso/openid_verify" method="post" id="Verify">' +
					'<input type="hidden" name="id_token" value="'+jwt+'">' +
					'<input type="hidden" name="login_url" value="">' +
					'<input type="hidden" name="email" value="${_.escape(this.email)}">' +
				'</form>'
			;
			document.getElementById('Verify').submit();
		`;
	}

	// build the callback for testing SSO success
	_getSsoCallback(win) {
		return values => {
			this.Logger.write('APP_LOGIN_DEBUG', `Got SSO JWT for email ${this.email}`, {
				values,
				btFile: 'loginComponent.js',
				btLine: 309,
			});
			if (!values[0]) {
				// A remote SSO login page, not our sso success page
				return;
			}
			const jwt = JSON.parse(values[0]);
			this.Auth.setToken(jwt);
			this._track('login_success', { type: 'sso' });
			this.postLogin();
			win.close();
			clearTimeout(this.errorTimeout);
		};
	}

	logIn() {
		this.Logger.write(
			'APP_LOGIN_DEBUG',
			`Submitting password for ${this.email} with length ${this.password ? this.password.length : 0}`,
			{
				btFile: 'loginComponent.js',
				btLine: 328,
			}
		);
		this.passwordError = '';
		this.Auth.login(this.email, this.password, this.rememberMe).then(
			data => {
				if (data.success) {
					this.Logger.write('APP_LOGIN_DEBUG', `Got correct password for ${this.email}, forwarding to post listing`, {
						data,
						btFile: 'loginComponent.js',
						btLine: 336,
					});
					this._track('login_success', { type: 'password' });
					this.postLogin();
				} else {
					this.Logger.write('APP_LOGIN_DEBUG', `Got incorrect password for ${this.email}, asking for password again`, {
						data,
						btFile: 'loginComponent.js',
						btLine: 345,
					});
					this._track('login_fail', { type: 'password' });
					this.passwordError = data.message || 'That password was incorrect. Please try again.';
				}
			},
			() => {
				this.Logger.write('APP_LOGIN_DEBUG', `Network error checking password`, {
					btFile: 'loginComponent.js',
					btLine: 353,
				});
				(navigator.notification || window).alert('Network error checking password. Please try again.');
			}
		);
	}

	requestForgotPasswordEmail() {
		this.forgotError = '';
		this.forgotSuccess = '';
		if (this.email && this.email.length === 0) {
			this.forgotError = 'Please enter a valid email address and try again.';
			return;
		}
		this.Auth.sendResetLink(this.email).then(
			resp => {
				if (resp.success) {
					this.forgotSuccess = resp.message;
					this.EventTracker.save('forgot_password', {
						result: 'Success',
						message: resp.message,
					});
				} else {
					this.forgotError = resp.message;
					this.EventTracker.save('forgot_password', {
						result: 'Error',
						message: resp.message,
					});
				}
				this.$timeout(() => {});
			},
			resp => {
				const message = resp.message || 'Unknown error occurred. Please retry.';
				this.forgotError = message;
				this.EventTracker.save('forgot_password', { result: 'Error', message });
			}
		);
	}

	postLogin() {
		if (this.onAfterLogin) {
			this.onAfterLogin({ $email: this.email });
		} else if (this.forwardTo) {
			if (/^https?:\/\//i.test(this.forwardTo)) {
				this.$timeout(() => {
					window.location.href = this.forwardTo;
				});
			} else {
				this.$timeout(() => {
					if (this.forwardTo === 'hub.loggedin.PostListing') {
						this.Api.get('/config/preferences')
							.then(response => {
								let homePage = response['Features.home_page'] || '/hub#/posts';
								//in case someone wants to use the classic insights page. This should be temporary
								if (response['UI.use_classic_insights_page'] === '1' && homePage.includes('core/insights')) {
									homePage = '/hub#/posts';
								}

								window.location.href = `${window.BASE_URL}${homePage}`;
							})
							.catch(() => {
								window.location.href = this.$state.href(this.forwardTo);
							});
					} else {
						window.location.href = this.$state.href(this.forwardTo);
					}
					window.location.href = this.$state.href(this.forwardTo);
				});
			}
		} else {
			throw new Error('<sh-login> requires that forward-to or on-after-login attributes be provided.');
		}
	}

	/**
	 * Show the cookie settings modal
	 */
	cookieSettingsPopup() {
		this.modal.state('hub.SettingsCookiesAnonymous', {
			heading: 'Cookie Settings',
			width: 620,
			height: 'full',
		});
	}

	/**
	 * Hides the cookie banner if they already accepted or declined non-necessary cookies
	 * @returns Boolean
	 */
	showCookieBanner() {
		return !this.Cookies.hasConsentCookie();
	}

	acceptAllCookies() {
		this.Cookies.updateOptionalConsent(true);
	}
}
